import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Sparkles, X, RefreshCw, Search, AlertCircle, MessageCircle, Send, User, Bot } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem("apex_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/** Smart Portfolio Analyzer — appelle Claude sur le portefeuille complet (async + polling) */
export function SmartPortfolioAnalyzer({ portfolio, owner, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progressLabel, setProgressLabel] = useState("");
  const pollIntervalRef = useRef(null);
  // 💬 Chat state (follow-up on the analysis)
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const startPolling = (taskId, seededMeta) => {
    let elapsed = 0;
    pollIntervalRef.current = setInterval(async () => {
      elapsed += 3;
      setProgressLabel(`Claude analyse ton portefeuille... (${elapsed}s)`);
      try {
        const r = await axios.get(
          `${API}/api/smart-analyzer/portfolio/status/${taskId}`,
          { headers: authHeaders(), timeout: 15000 }
        );
        const st = r.data?.status;
        if (st === "done") {
          stopPolling();
          setData({ ...seededMeta, ...r.data });
          setLoading(false);
          setProgressLabel("");
        } else if (st === "error") {
          stopPolling();
          setError(r.data?.error || "Erreur d'analyse");
          setLoading(false);
          setProgressLabel("");
        }
        // else keep polling (status === "running")
      } catch (e) {
        // 404 = task expired / not found → stop
        if (e?.response?.status === 404) {
          stopPolling();
          setError("Tâche expirée. Relance l'analyse.");
          setLoading(false);
          setProgressLabel("");
        }
        // Other transient errors → keep polling
      }
      // Safety cap: 4 min max
      if (elapsed >= 240) {
        stopPolling();
        setError("Timeout (4 min). Relance l'analyse.");
        setLoading(false);
        setProgressLabel("");
      }
    }, 3000);
  };

  const run = async () => {
    stopPolling();
    setLoading(true);
    setError(null);
    setData(null);
    setChatMessages([]);
    setChatError(null);
    setProgressLabel("Envoi du portefeuille à Claude...");
    try {
      const positions = (portfolio || []).map(p => ({
        ticker: p.ticker,
        name: p.name,
        qty: p.qty,
        avg_price: p.avg,
        current_price: p.current_price || p.last_price || p.avg,
        sector: p.sector,
        strategy: p.strategy || "LT",
      }));
      const r = await axios.post(`${API}/api/smart-analyzer/portfolio`,
        { owner, positions },
        { headers: authHeaders(), timeout: 20000 }
      );
      // Response is now `{ok, task_id, status: "started", ...aggregates}`
      const taskId = r.data?.task_id;
      if (!taskId) {
        // Legacy fallback: if server returns analysis inline
        if (r.data?.analysis) {
          setData(r.data);
          setLoading(false);
          setProgressLabel("");
          return;
        }
        throw new Error("Aucun task_id retourné");
      }
      setProgressLabel("Claude analyse ton portefeuille... (0s)");
      startPolling(taskId, {
        owner: r.data.owner,
        total_value: r.data.total_value,
        total_cost: r.data.total_cost,
        pnl_pct: r.data.pnl_pct,
        positions_count: r.data.positions_count,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Erreur");
      setLoading(false);
      setProgressLabel("");
    }
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || !data?.analysis || chatLoading) return;
    setChatLoading(true); setChatError(null);
    const newUserMsg = { role: "user", content: msg };
    const updatedMessages = [...chatMessages, newUserMsg];
    setChatMessages(updatedMessages);
    setChatInput("");
    try {
      const positions = (portfolio || []).map(p => ({
        ticker: p.ticker,
        qty: p.qty,
        avg_price: p.avg,
        current_price: p.current_price || p.last_price || p.avg,
      }));
      const r = await axios.post(`${API}/api/smart-analyzer/portfolio-chat`,
        {
          owner,
          positions,
          original_analysis: data.analysis,
          messages: chatMessages,
          new_message: msg,
        },
        { headers: authHeaders(), timeout: 90000 }
      );
      setChatMessages([
        ...updatedMessages,
        { role: "assistant", content: r.data.reply, tickers: r.data.tickers_refreshed || [] },
      ]);
    } catch (e) {
      setChatError(e?.response?.data?.detail || e.message || "Erreur chat");
      setChatMessages(chatMessages);
      setChatInput(msg);
    } finally { setChatLoading(false); }
  };

  return (
    <div className="glass-card rounded-2xl border border-purple-500/30 overflow-hidden" data-testid="smart-portfolio-analyzer">
      <div className="bg-gradient-to-r from-purple-600/20 via-fuchsia-600/15 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/20 border border-purple-400/40">
            <Sparkles size={18} className="text-purple-300" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">Smart Analysis — Portefeuille {owner}</h3>
            <p className="text-xs text-slate-400">Framework Claude Sonnet 4.5 · Diagnostic global + actions</p>
          </div>
        </div>
        <div className="flex gap-2">
          {data && (
            <button onClick={run} disabled={loading} className="p-2 rounded-lg bg-slate-700/40 hover:bg-slate-600/40 text-slate-300 disabled:opacity-50" data-testid="smart-portfolio-refresh">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-lg bg-slate-700/40 hover:bg-slate-600/40 text-slate-300" data-testid="smart-portfolio-close">
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      <div className="p-5">
        {!data && !loading && (
          <div className="flex flex-col items-center py-8 gap-3">
            <p className="text-sm text-slate-400 text-center">Lance une analyse approfondie de ton portefeuille par Claude.<br/>Diagnostic, positions à garder/vendre, risques, action prioritaire.</p>
            <button onClick={run} className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white font-semibold text-sm" data-testid="smart-portfolio-run">
              Analyser mon portefeuille
            </button>
            <p className="text-[10px] text-slate-500">~30-60s via Claude Sonnet 4.5</p>
          </div>
        )}
        {loading && (
          <div className="flex flex-col items-center py-12 gap-3">
            <RefreshCw size={24} className="text-purple-300 animate-spin" />
            <p className="text-sm text-slate-300">{progressLabel || "Claude analyse ton portefeuille..."}</p>
            <p className="text-xs text-slate-500">Diagnostic, positions à garder/vendre, risques, action prioritaire</p>
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-rose-500/10 border border-rose-400/30 rounded-lg" data-testid="smart-portfolio-error">
            <AlertCircle size={16} className="text-rose-300 mt-0.5 shrink-0" />
            <p className="text-sm text-rose-200">{error}</p>
          </div>
        )}
        {data && (
          <div className="space-y-4" data-testid="smart-portfolio-content">
            <div className="prose prose-invert prose-sm max-w-none">
              <div className="mb-3 flex gap-3 text-xs text-slate-400 flex-wrap">
                <span>Valeur : <b className="text-slate-200">${data.total_value?.toLocaleString("fr-FR")}</b></span>
                <span>PnL global : <b className={data.pnl_pct >= 0 ? "text-emerald-300" : "text-rose-300"}>{data.pnl_pct >= 0 ? "+" : ""}{data.pnl_pct?.toFixed(2)}%</b></span>
                <span>Positions : <b className="text-slate-200">{data.positions_count}</b></span>
              </div>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.analysis}</ReactMarkdown>
            </div>

            {/* 💬 Chat de suivi sur l'analyse portfolio */}
            <div className="border-t border-slate-700 pt-4 space-y-3" data-testid="smart-portfolio-chat">
              <div className="flex items-center gap-2 text-slate-300">
                <MessageCircle size={14} className="text-purple-300" />
                <span className="text-xs uppercase tracking-wider font-semibold">Continuer la discussion</span>
                {chatMessages.length > 0 && (
                  <span className="text-[10px] text-slate-500">({chatMessages.filter(m => m.role === "assistant").length} réponses)</span>
                )}
              </div>

              {chatMessages.length > 0 && (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                  {chatMessages.map((m, i) => (
                    <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`smart-portfolio-chat-msg-${i}`}>
                      {m.role === "assistant" && (
                        <div className="shrink-0 p-1.5 rounded-full bg-purple-500/20 border border-purple-400/40 self-start">
                          <Bot size={12} className="text-purple-300" />
                        </div>
                      )}
                      <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                        m.role === "user"
                          ? "bg-fuchsia-600/25 border border-fuchsia-500/40 text-slate-100"
                          : "bg-slate-800/70 border border-slate-600/40 text-slate-200"
                      }`}>
                        {m.role === "user" ? (
                          <p className="whitespace-pre-wrap">{m.content}</p>
                        ) : (
                          <>
                            <div className="prose prose-invert prose-sm max-w-none">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                            </div>
                            {m.tickers && m.tickers.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-700 flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
                                <span>📊 Prix live utilisés :</span>
                                {m.tickers.map(t => (
                                  <span key={t} className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/30 font-mono">{t}</span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {m.role === "user" && (
                        <div className="shrink-0 p-1.5 rounded-full bg-fuchsia-600/25 border border-fuchsia-500/40 self-start">
                          <User size={12} className="text-fuchsia-300" />
                        </div>
                      )}
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex gap-2 justify-start">
                      <div className="shrink-0 p-1.5 rounded-full bg-purple-500/20 border border-purple-400/40 self-start">
                        <Bot size={12} className="text-purple-300" />
                      </div>
                      <div className="px-3 py-2 rounded-lg bg-slate-800/70 border border-slate-600/40 text-slate-300 text-sm flex items-center gap-2">
                        <RefreshCw size={12} className="animate-spin text-purple-300" />
                        <span className="text-xs">Claude réfléchit...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {chatError && (
                <div className="flex items-start gap-2 p-2 bg-rose-500/10 border border-rose-400/30 rounded-lg" data-testid="smart-portfolio-chat-error">
                  <AlertCircle size={14} className="text-rose-300 mt-0.5 shrink-0" />
                  <p className="text-xs text-rose-200">{chatError}</p>
                </div>
              )}

              <div className="flex items-end gap-2">
                <textarea
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  rows={2}
                  placeholder="Ex: Détaille pourquoi vendre CRWV ? Combien alléger MU ? Que penses-tu de rotate vers energy ? Quel est le vrai risque #1 ?"
                  className="flex-1 px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-purple-400/60 resize-none"
                  disabled={chatLoading}
                  data-testid="smart-portfolio-chat-input"
                />
                <button
                  onClick={sendChat}
                  disabled={chatLoading || !chatInput.trim()}
                  className="px-3 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-1.5"
                  data-testid="smart-portfolio-chat-send"
                >
                  <Send size={14} />
                  <span className="hidden sm:inline">Envoyer</span>
                </button>
              </div>
              <p className="text-[10px] text-slate-500 italic">
                💡 Enter pour envoyer · Shift+Enter pour aller à la ligne · Claude garde le contexte de ton portefeuille et utilise les prix live yfinance
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Smart Finder — demande à Claude de trouver des actions selon une description */
export function SmartFinderAnalyzer({ portfolio, owner, onClose }) {
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 💬 Chat state (follow-up conversation on the ideas)
  const [chatMessages, setChatMessages] = useState([]); // [{role:'user'|'assistant', content:str, tickers?:[]}]
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);

  const run = async () => {
    if (!query.trim()) return;
    setLoading(true); setError(null); setChatMessages([]); setChatError(null);
    try {
      // Build portfolio summary for context
      const summary = (portfolio || []).slice(0, 30).map(p => `${p.ticker} (${p.sector || "?"})`).join(", ");
      const sectors_held = [...new Set((portfolio || []).map(p => p.sector).filter(Boolean))];
      const r = await axios.post(`${API}/api/smart-analyzer/find-opportunity`,
        { query, context: { portfolio_summary: summary, sectors_held } },
        { headers: authHeaders(), timeout: 120000 }
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Erreur");
    } finally { setLoading(false); }
  };

  const runBestOpps = async () => {
    setLoading(true); setError(null); setChatMessages([]); setChatError(null);
    try {
      const r = await axios.get(`${API}/api/smart-opportunities`, {
        params: { owner: owner || "Helmi" },
        headers: authHeaders(),
        timeout: 120000,
      });
      // Adapt response shape to match existing renderer (uses data.ideas)
      setData({ ...r.data, ideas: r.data.analysis });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Erreur");
    } finally { setLoading(false); }
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || !data?.ideas || chatLoading) return;
    setChatLoading(true); setChatError(null);
    const newUserMsg = { role: "user", content: msg };
    const updatedMessages = [...chatMessages, newUserMsg];
    setChatMessages(updatedMessages);
    setChatInput("");
    try {
      const r = await axios.post(`${API}/api/smart-analyzer/finder-chat`,
        {
          original_query: query || "(top opportunités du jour)",
          original_ideas: data.ideas,
          messages: chatMessages,       // send prior history (excludes the new msg, backend will see it as new_message)
          new_message: msg,
        },
        { headers: authHeaders(), timeout: 90000 }
      );
      setChatMessages([
        ...updatedMessages,
        { role: "assistant", content: r.data.reply, tickers: r.data.tickers_refreshed || [] },
      ]);
    } catch (e) {
      setChatError(e?.response?.data?.detail || e.message || "Erreur chat");
      // rollback: remove the user message we just added
      setChatMessages(chatMessages);
      setChatInput(msg); // restore user input so they can retry
    } finally { setChatLoading(false); }
  };

  return (
    <div className="glass-card rounded-2xl border border-cyan-500/30 overflow-hidden" data-testid="smart-finder">
      <div className="bg-gradient-to-r from-cyan-600/20 via-blue-600/15 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-500/20 border border-cyan-400/40">
            <Search size={18} className="text-cyan-300" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">Trouve-moi une action</h3>
            <p className="text-xs text-slate-400">Claude propose 2-4 idées selon tes critères</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-2 rounded-lg bg-slate-700/40 hover:bg-slate-600/40 text-slate-300" data-testid="smart-finder-close">
            <X size={16} />
          </button>
        )}
      </div>
      <div className="p-5 space-y-4">
        {/* 🏆 One-click best opportunities — agrégation automatique de toutes les données app */}
        <button
          onClick={runBestOpps}
          disabled={loading}
          className="w-full px-5 py-3.5 rounded-lg bg-gradient-to-r from-fuchsia-600 via-purple-600 to-cyan-600 hover:brightness-110 text-white font-bold text-sm shadow-glow disabled:opacity-40 flex items-center justify-center gap-2 transition-all"
          data-testid="smart-finder-best-opps"
        >
          {loading ? (
            <><RefreshCw size={16} className="animate-spin" /> Claude analyse l&apos;app...</>
          ) : (
            <><Sparkles size={16} /> 🏆 Top opportunités du jour <span className="text-[10px] opacity-75 font-normal">(scan + news + portfolio + market)</span></>
          )}
        </button>

        <div className="relative flex items-center gap-3 py-1">
          <div className="flex-1 h-px bg-slate-700/50" />
          <span className="text-[10px] uppercase text-slate-500 tracking-wider">ou recherche libre</span>
          <div className="flex-1 h-px bg-slate-700/50" />
        </div>

        <div>
          <label className="text-xs text-slate-400 uppercase tracking-wider">Que cherches-tu ?</label>
          <textarea
            value={query}
            onChange={e => setQuery(e.target.value)}
            rows={3}
            placeholder="Ex: Action tech mid-cap qui n'a pas encore explosé, momentum en cours, earnings dans 2-4 semaines. Ou: Une biotech en pre-FDA avec un risk/reward asymétrique. Ou: Quel semi-conducteur acheter maintenant si je crois au cycle AI ?"
            className="w-full mt-1 px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-400/60"
            data-testid="smart-finder-query"
          />
        </div>
        <div className="flex justify-end">
          <button onClick={run} disabled={loading || !query.trim()} className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold text-sm disabled:opacity-40" data-testid="smart-finder-run">
            {loading ? <><RefreshCw size={14} className="inline animate-spin mr-1.5" />Recherche...</> : <><Sparkles size={14} className="inline mr-1.5" />Trouver des idées</>}
          </button>
        </div>
        {error && (
          <div className="flex items-start gap-2 p-3 bg-rose-500/10 border border-rose-400/30 rounded-lg" data-testid="smart-finder-error">
            <AlertCircle size={16} className="text-rose-300 mt-0.5 shrink-0" />
            <p className="text-sm text-rose-200">{error}</p>
          </div>
        )}
        {data && (
          <div className="prose prose-invert prose-sm max-w-none border-t border-slate-700 pt-4" data-testid="smart-finder-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.ideas}</ReactMarkdown>
          </div>
        )}

        {/* 💬 Chat de suivi — apparaît une fois qu'une reco a été donnée */}
        {data && (
          <div className="border-t border-slate-700 pt-4 space-y-3" data-testid="smart-finder-chat">
            <div className="flex items-center gap-2 text-slate-300">
              <MessageCircle size={14} className="text-cyan-400" />
              <span className="text-xs uppercase tracking-wider font-semibold">Continuer la discussion</span>
              {chatMessages.length > 0 && (
                <span className="text-[10px] text-slate-500">({chatMessages.filter(m => m.role === "assistant").length} réponses)</span>
              )}
            </div>

            {/* Chat history */}
            {chatMessages.length > 0 && (
              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                {chatMessages.map((m, i) => (
                  <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`smart-finder-chat-msg-${i}`}>
                    {m.role === "assistant" && (
                      <div className="shrink-0 p-1.5 rounded-full bg-cyan-500/20 border border-cyan-400/40 self-start">
                        <Bot size={12} className="text-cyan-300" />
                      </div>
                    )}
                    <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                      m.role === "user"
                        ? "bg-blue-600/25 border border-blue-500/40 text-slate-100"
                        : "bg-slate-800/70 border border-slate-600/40 text-slate-200"
                    }`}>
                      {m.role === "user" ? (
                        <p className="whitespace-pre-wrap">{m.content}</p>
                      ) : (
                        <>
                          <div className="prose prose-invert prose-sm max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                          </div>
                          {m.tickers && m.tickers.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-slate-700 flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
                              <span>📊 Prix live utilisés :</span>
                              {m.tickers.map(t => (
                                <span key={t} className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 font-mono">{t}</span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {m.role === "user" && (
                      <div className="shrink-0 p-1.5 rounded-full bg-blue-600/25 border border-blue-500/40 self-start">
                        <User size={12} className="text-blue-300" />
                      </div>
                    )}
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="shrink-0 p-1.5 rounded-full bg-cyan-500/20 border border-cyan-400/40 self-start">
                      <Bot size={12} className="text-cyan-300" />
                    </div>
                    <div className="px-3 py-2 rounded-lg bg-slate-800/70 border border-slate-600/40 text-slate-300 text-sm flex items-center gap-2">
                      <RefreshCw size={12} className="animate-spin text-cyan-400" />
                      <span className="text-xs">Claude réfléchit...</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {chatError && (
              <div className="flex items-start gap-2 p-2 bg-rose-500/10 border border-rose-400/30 rounded-lg" data-testid="smart-finder-chat-error">
                <AlertCircle size={14} className="text-rose-300 mt-0.5 shrink-0" />
                <p className="text-xs text-rose-200">{chatError}</p>
              </div>
            )}

            {/* Chat input */}
            <div className="flex items-end gap-2">
              <textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendChat();
                  }
                }}
                rows={2}
                placeholder="Ex: Et toi qu'est-ce que tu suggères ? Compare MO vs VZ. Pourquoi pas LMT ? Quel est le vrai risque de #2 ?"
                className="flex-1 px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-400/60 resize-none"
                disabled={chatLoading}
                data-testid="smart-finder-chat-input"
              />
              <button
                onClick={sendChat}
                disabled={chatLoading || !chatInput.trim()}
                className="px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-1.5"
                data-testid="smart-finder-chat-send"
              >
                <Send size={14} />
                <span className="hidden sm:inline">Envoyer</span>
              </button>
            </div>
            <p className="text-[10px] text-slate-500 italic">
              💡 Enter pour envoyer · Shift+Enter pour aller à la ligne · Claude conserve le contexte des idées initiales et utilise les prix live yfinance
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
